import { confirmToast } from "../../utils/toast";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import DealNavigation from "../../components/DealNavigation";
import DealInfoPanel from "../../components/DealInfoPanel";
import BuyerDetailModal from "../../components/BuyerDetailModal";
import ShipperDetailModal from "../../components/ShipperDetailModal";
import FollowupModal from "../../components/FollowupModal";
import FixationModal from "../../components/FixationModal";
import { buildFixationPricingDefaults } from "../../utils/fixationDefaults";

const DOCUMENT_TYPES = ["Sales Contract", "Purchase Contract", "Advance Payment", "Invoice", "Bill of Lading", "Fixation Note", "Other"];
const ATTACHMENT_API = "/sales-contract/attachments";
const MAX_FILE_SIZE = 15 * 1024 * 1024;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
  reader.onerror = () => reject(new Error("Unable to read the selected file"));
  reader.readAsDataURL(file);
});

const base64ToBlob = (base64Data, mimeType) => {
  const binary = window.atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType || "application/octet-stream" });
};

const formatSize = (value) => {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const Attachment = () => {
  const { dealId } = useParams();
  const [deal, setDeal] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [documentType, setDocumentType] = useState("Sales Contract");
  const [remark, setRemark] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [showDealInfo, setShowDealInfo] = useState(true);
  const [showBuyer, setShowBuyer] = useState(false);
  const [showShipper, setShowShipper] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [showFixation, setShowFixation] = useState(false);
  const fixationPricingDefaults = useMemo(() => buildFixationPricingDefaults(deal), [deal]);

  const clearStatus = () => {
    setMessage("");
    setError("");
  };

  const loadAttachments = useCallback(async () => {
    const response = await axios.get(`${ATTACHMENT_API}/deal/${dealId}`);
    setAttachments(response.data || []);
  }, [dealId]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [dealResponse, attachmentResponse] = await Promise.all([
          axios.get(`/container-release/deal/${dealId}`),
          axios.get(`${ATTACHMENT_API}/deal/${dealId}`)
        ]);
        if (!active) return;
        setDeal(dealResponse.data || {});
        setAttachments(attachmentResponse.data || []);
      } catch (requestError) {
        if (active) setError(requestError.response?.data?.message || "Unable to load attachments");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [dealId]);

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const saveBase64 = async (payload) => {
    const fields = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) fields.append(key, String(value));
    });
    await axios.post(`${ATTACHMENT_API}/deal/${dealId}/base64`, fields);
    await loadAttachments();
  };

  const generateSalesContract = async () => {
    clearStatus();
    setBusy(true);
    try {
      const response = await axios.post(`/sales-contract/deal/${dealId}/generate-pdf`);
      await saveBase64({ ...response.data, remark: "Generated from Sales Contract" });
      setMessage("Sales Contract PDF generated and attached successfully.");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to generate the Sales Contract PDF");
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async () => {
    clearStatus();
    if (!selectedFile) {
      setError("Please select a file first.");
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError("Each file must be 15 MB or smaller.");
      return;
    }
    setBusy(true);
    try {
      const base64Data = await fileToBase64(selectedFile);
      await saveBase64({
        documentType,
        fileName: selectedFile.name,
        mimeType: selectedFile.type || "application/octet-stream",
        fileSize: selectedFile.size,
        remark,
        base64Data
      });
      setSelectedFile(null);
      setRemark("");
      const input = document.getElementById("deal-attachment-file");
      if (input) input.value = "";
      setMessage("Attachment uploaded successfully.");
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Unable to upload attachment");
    } finally {
      setBusy(false);
    }
  };

  const loadContent = async (attachment) => {
    const response = await axios.get(`${ATTACHMENT_API}/${attachment.AttachmentID}/content`);
    const blob = base64ToBlob(response.data.base64Data, response.data.mimeType);
    return { blob, data: response.data };
  };

  const previewAttachment = async (attachment) => {
    clearStatus();
    setBusy(true);
    try {
      const { blob, data } = await loadContent(attachment);
      const url = URL.createObjectURL(blob);
      setPreview((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return { url, fileName: data.fileName, mimeType: data.mimeType };
      });
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to preview attachment");
    } finally {
      setBusy(false);
    }
  };

  const downloadAttachment = async (attachment) => {
    clearStatus();
    setBusy(true);
    try {
      const { blob, data } = await loadContent(attachment);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = data.fileName || attachment.OriginalName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to download attachment");
    } finally {
      setBusy(false);
    }
  };

  const deleteAttachment = async (attachment) => {
    if (!await confirmToast(`Delete ${attachment.OriginalName}?`)) return;
    clearStatus();
    setBusy(true);
    try {
      await axios.delete(`${ATTACHMENT_API}/${attachment.AttachmentID}`);
      if (preview?.fileName === attachment.OriginalName) setPreview(null);
      await loadAttachments();
      setMessage("Attachment deleted successfully.");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to delete attachment");
    } finally {
      setBusy(false);
    }
  };

  const canPreviewInline = useMemo(
    () => preview && (preview.mimeType === "application/pdf" || preview.mimeType?.startsWith("image/")),
    [preview]
  );

  return (
    <>
      <DealNavigation
        dealId={dealId}
        openBuyer={() => setShowBuyer(true)}
        openShipper={() => setShowShipper(true)}
        openFixation={() => setShowFixation(true)}
        openFollowup={() => setShowFollowup(true)}
      />

      <div className="container-fluid mt-3">
        <div className="mb-2">
          <span className="badge bg-success fs-6 px-3 py-2">Attachment</span>
        </div>

        <DealInfoPanel deal={deal} show={showDealInfo} onToggle={() => setShowDealInfo((value) => !value)} />

        {error && <div className="alert alert-danger py-2">{error}</div>}
        {message && <div className="alert alert-success py-2">{message}</div>}

        <div className="border rounded bg-white mb-3">
          <div className="px-3 py-2 border-bottom fw-bold text-muted">Generate Sales Contract PDF</div>
          <div className="p-3 d-flex align-items-center gap-3">
            <button type="button" className="btn btn-success btn-sm" onClick={generateSalesContract} disabled={busy}>
              {busy ? "Please wait..." : "Generate & Attach PDF"}
            </button>
            <span className="text-muted small">Creates the fixed-layout contract, converts it to base64, and saves it here.</span>
          </div>
        </div>

        <div className="border rounded bg-white mb-3">
          <div className="px-3 py-2 border-bottom fw-bold text-muted">Upload Attachment</div>
          <div className="p-3 row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Document Type</label>
              <select className="form-select form-select-sm" value={documentType} onChange={(event) => setDocumentType(event.target.value)}>
                {DOCUMENT_TYPES.map((type) => <option key={type}>{type}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">File</label>
              <input id="deal-attachment-file" type="file" className="form-control form-control-sm" onChange={(event) => setSelectedFile(event.target.files?.[0] || null)} />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Remark</label>
              <input className="form-control form-control-sm" value={remark} onChange={(event) => setRemark(event.target.value)} />
            </div>
            <div className="col-md-2">
              <button type="button" className="btn btn-primary btn-sm" onClick={uploadFile} disabled={busy}>Upload Base64</button>
            </div>
          </div>
        </div>

        <div className="border rounded bg-white mb-3">
          <div className="px-3 py-2 border-bottom fw-bold text-muted">Attached Documents</div>
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Document Type</th><th>File Name</th><th>Size</th><th>Remark</th><th>Uploaded At</th><th style={{ width: 220 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-3">Loading...</td></tr>
                ) : attachments.length === 0 ? (
                  <tr><td colSpan="6" className="text-center text-muted py-3">No attachment available. Click “Generate & Attach PDF”.</td></tr>
                ) : attachments.map((attachment) => (
                  <tr key={attachment.AttachmentID}>
                    <td>{attachment.DocumentType || "Other"}</td>
                    <td>{attachment.OriginalName}</td>
                    <td>{formatSize(attachment.FileSize)}</td>
                    <td>{attachment.Remark || ""}</td>
                    <td>{attachment.UploadedAt ? new Date(attachment.UploadedAt).toLocaleString() : ""}</td>
                    <td>
                      <button className="btn btn-outline-primary btn-sm me-1" onClick={() => previewAttachment(attachment)} disabled={busy}>Preview</button>
                      <button className="btn btn-outline-success btn-sm me-1" onClick={() => downloadAttachment(attachment)} disabled={busy}>Download</button>
                      <button className="btn btn-outline-danger btn-sm" onClick={() => deleteAttachment(attachment)} disabled={busy}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {preview && (
          <div className="border rounded bg-white mb-4">
            <div className="px-3 py-2 border-bottom fw-bold d-flex justify-content-between">
              <span>Preview: {preview.fileName}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setPreview(null)}>Close</button>
            </div>
            {canPreviewInline ? (
              <iframe title={preview.fileName} src={preview.url} style={{ width: "100%", height: "780px", border: 0 }} />
            ) : (
              <div className="p-3 text-muted">Preview is not supported for this file type. Use Download.</div>
            )}
          </div>
        )}
      </div>

      <BuyerDetailModal show={showBuyer} dealId={dealId} onClose={() => setShowBuyer(false)} />
      <ShipperDetailModal show={showShipper} dealId={dealId} onClose={() => setShowShipper(false)} />
      <FixationModal
        show={showFixation}
        dealId={dealId}
        totalQty={deal.TotalQty}
        dealCurrency={deal.DealCurrency}
        dealPricingDefaults={fixationPricingDefaults}
        onClose={() => {
          setShowFixation(false);
          loadAttachments().catch(() => setError("Unable to refresh attachments"));
        }}
      />
      <FollowupModal show={showFollowup} dealId={dealId} onClose={() => setShowFollowup(false)} />
    </>
  );
};

export default Attachment;
